import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import { Container, Grid, TextField, Button, Chip, CircularProgress, Typography, Pagination } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import AddIcon from '@mui/icons-material/Add';
import Search from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import HeaderMenu from '../../components/header-menu';
import Footer from '../../components/footer';
import { Overlay } from '../../styles/global';
import { Styles } from '../../styles/material-styles';
import MyMaterialTable from '../../components/table';
import CustomizedSnackbars from '../../components/material-snackbars';
import * as Service from '../../services/tasktemplate.service';

const useStyles = makeStyles(Styles);
const PAGE_SIZE = 10;

export default function TaskTemplateList() {
  const classes = useStyles();
  const history = useHistory();
  const { lang } = useParams();
  const headerMenu = useSelector(state => state.headerMenu);
  const actionsHeaderMenu = useSelector(state => state.actionsHeaderMenu);

  const [resultList, setResultList] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, variant: 'success', message: '' });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageStart, setPageStart] = useState(0);
  const [pageEnd, setPageEnd] = useState(0);
  const [count, setCount] = useState(0);

  const activeSearch = useRef({ field: '', value: '' });

  useEffect(() => { loadPage(1, '', ''); }, []);

  const applyPagination = (total, pageNum) => {
    const pages = Math.ceil(total / PAGE_SIZE);
    setTotalPages(pages);
    setCount(total);
    if (total > 0) {
      setPageStart((pageNum - 1) * PAGE_SIZE + 1);
      setPageEnd(Math.min(pageNum * PAGE_SIZE, total));
    } else {
      setPageStart(0);
      setPageEnd(0);
    }
  };

  const loadPage = async (pageNum, field, search) => {
    setLoading(true);
    const result = await Service.paginate(pageNum, PAGE_SIZE, field, search);
    if (result.success) {
      setResultList(result.data);
      applyPagination(result.count ?? 0, pageNum);
      setPage(pageNum);
    } else {
      showNotification('error', 'Erro ao carregar templates de tarefa');
    }
    setLoading(false);
  };

  const showNotification = (variant, message) =>
    setNotification({ open: true, variant, message });

  const handleSearch = async () => {
    const trimmed = searchValue.trim();
    activeSearch.current = { field: trimmed ? 'name' : '', value: trimmed };
    await loadPage(1, activeSearch.current.field, activeSearch.current.value);
  };

  const handleClear = async () => {
    setSearchValue('');
    activeSearch.current = { field: '', value: '' };
    await loadPage(1, '', '');
  };

  const handlePageChange = (_e, newPage) => {
    loadPage(newPage, activeSearch.current.field, activeSearch.current.value);
  };

  const handleDelete = async (data, resolve) => {
    const result = await Service.deleteById(data.id);
    if (result.success) {
      showNotification('success', 'Template removido com sucesso');
      await loadPage(page, activeSearch.current.field, activeSearch.current.value);
    } else {
      showNotification('error', 'Erro ao remover template');
    }
    resolve();
  };

  const columns = [
    { title: 'ID', field: 'id', width: '10%' },
    { title: 'Nome', field: 'name', width: '70%' },
    {
      title: 'Ativo',
      field: 'active',
      width: '20%',
      render: row => (
        <Chip
          label={row.active ? 'Ativo' : 'Inativo'}
          color={row.active ? 'success' : 'default'}
          size="small"
        />
      ),
    },
  ];

  return (
    <div className={classes.root}>
      <HeaderMenu />
      <main
        className={clsx(
          classes.content,
          { [classes.contentShift]: headerMenu.open },
          classes.actionsContent,
          { [classes.actionscontentShift]: actionsHeaderMenu.open }
        )}
      >
        <div className={classes.drawerHeader} />
        <Container component="main" maxWidth="xl" style={{ paddingLeft: 0, marginLeft: 0 }}>
          <Grid container className={classes.mb20} spacing={0}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              className={classes.button}
              startIcon={<AddIcon />}
              onClick={() => history.push(`/${lang}/task-template-registration`)}
            >
              Adicionar
            </Button>
          </Grid>

          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} sm={12}>
              <Card className="background-title-search">
                <CardContent>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Typography variant="h5">Templates de Tarefa do Chamado</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                      <Search />
                      <TextField
                        type="text"
                        name="searchValue"
                        value={searchValue}
                        placeholder="Buscar por nome"
                        onChange={e => setSearchValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                      />
                      <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        className={classes.button}
                        onClick={handleSearch}
                      >
                        <Search />
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        className={classes.button}
                        onClick={handleClear}
                      >
                        <ClearIcon />
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <MyMaterialTable
            title="Templates de Tarefa do Chamado"
            rowClick={(_event, rowData) => {
              history.push(`/${lang}/task-template-edit/${rowData.id}`);
            }}
            columns={columns}
            data={resultList}
            deleteItem={handleDelete}
          />

          <Grid container style={{ marginBottom: 12, marginTop: 8 }}>
            <Grid item xs={12} md={3}>
              <Pagination count={totalPages} page={page} onChange={handlePageChange} />
            </Grid>
            <Grid item xs={12} sm={3} style={{ marginTop: 6 }}>
              {pageStart} a {pageEnd} de {count} registros
            </Grid>
          </Grid>
        </Container>
      </main>

      {loading && <Overlay><CircularProgress color="secondary" /></Overlay>}

      <CustomizedSnackbars
        variant={notification.variant}
        message={notification.message}
        isOpen={notification.open}
        toClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
      <Footer />
    </div>
  );
}
